import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataGrid, { Column, MasterDetail, Editing, RequiredRule, Selection } from 'devextreme-react/data-grid';
import DropDownBox from 'devextreme-react/drop-down-box';
import { TextBox } from 'devextreme-react';
import { RowInsertingEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';

import { formatMessage } from 'devextreme/localization';

import ReportingGroupPathTemplate from './ReportingGroupPathTemplate';

import ReportingGroupApi from '../../api/ReportingGroupApi';
import CustomerApiCredentialApi from '../../api/CustomerApiCredentialApi';

import ReportingGroupDto from '../../classes/dtos/reportinggroupmodule/ReportingGroupDto';
import CustomerApiCredentialListDto from '../../classes/dtos/credentialmodule/CustomerApiCredentialListDto';

import './reporting-groups.scss';

function GridInDropDownCell({ cellInfo, customerApiCredentialListDto }: { cellInfo: any; customerApiCredentialListDto: CustomerApiCredentialListDto[] | null }) {
    const [selectedKeys, setSelectedKeys] = useState(cellInfo.value || []);
    const [isOpened, setIsOpened] = useState(false);

    const onSelectionChanged = (e: any) => {
        setSelectedKeys(e.selectedRowKeys);
        cellInfo.setValue(e.selectedRowKeys);
    };

    return (

        <DropDownBox
            value={selectedKeys}
            opened={isOpened}
            valueExpr="Id"
            placeholder={formatMessage("ReportingGroupsGrid_SelectACredential")}
            showClearButton={false}
            dataSource={customerApiCredentialListDto}
            onValueChanged={(e) => {
                setSelectedKeys(e.value || []);
                cellInfo.setValue(e.value || []);
            }}
            onOpenedChange={(opened) => setIsOpened(opened)}
            fieldRender={() => (
                <TextBox>
                    <div style={{ marginLeft: '16px', flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px', cursor: 'pointer' }}>
                        {(customerApiCredentialListDto || [])
                            .filter(cac => (cellInfo.value || []).includes(cac.Id))
                            .map((cac, idx, arr) => (
                                <span key={cac.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                                    {cac.Name}
                                </span>
                            ))
                        }
                        {(!cellInfo.value || cellInfo.value.length === 0) && (
                            <span>
                                {formatMessage("ReportingGroupsGrid_NoCredentials")}
                            </span>
                        )}
                    </div>
                </TextBox>
            )}
            contentRender={() => (
                <DataGrid
                    dataSource={customerApiCredentialListDto}
                    selectedRowKeys={selectedKeys}
                    onSelectionChanged={onSelectionChanged}
                    hoverStateEnabled={true}
                    height={300}
                    keyExpr="Id"
                    showBorders={true}
                >
                    <Selection mode="multiple" showCheckBoxesMode="always" />
                    <Column dataField="Name" caption="Name" minWidth={200} />
                    <Column dataField="TypeLabel" caption="Type" width={200} />
                    <Column dataField="ValidityStart" caption="Validity start" dataType="date" width={140} />
                    <Column dataField="ValidityEnd" caption="Validity end" dataType="date" width={140} />
                </DataGrid>
            )}
        />
    );
}

export default function ReportingGroups() {

    const [reportingGroups, setReportingGroups] = useState<ReportingGroupDto[] | null>(null);
    const [customerApiCredentialListDto, setCustomerApiCredentialListDto] = useState<CustomerApiCredentialListDto[] | null>(null);

    useEffect(() => {
        if (reportingGroups === null) {
            ReportingGroupApi.GetReportingGroupsAsync()
                .then(list => {
                    setReportingGroups(list.Result);
                });
        }
    }, [reportingGroups]);

    useEffect(() => {
        if (customerApiCredentialListDto === null) {
            CustomerApiCredentialApi.GetCustomerApiCredentialsAsync()
                .then(list => {
                    setCustomerApiCredentialListDto(list.Result);
                });
        }
    }, [customerApiCredentialListDto]);

    // La grille ne doit recevoir une nouvelle reference de dataSource que lorsque les donnees
    // changent reellement : sinon chaque rendu React reconfigure le widget DevExtreme.
    const gridDataSource = useMemo(() => reportingGroups ?? [], [reportingGroups]);

    const insertRow = useCallback((e: RowInsertingEvent<ReportingGroupDto, any>) => {
        ReportingGroupApi.CreateReportingGroupAsync(e.data.Name, e.data.CustomerApiCredentialIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setReportingGroups(null);
            });
    }, []);

    const updateRow = useCallback((e: RowUpdatingEvent<ReportingGroupDto, any>) => {
        let reportingGroupId = e.newData.ReportingGroupId ?? e.oldData.ReportingGroupId;
        let name = e.newData.Name ?? e.oldData.Name;
        let customerApiCredentialIds = e.newData.CustomerApiCredentialIds ?? e.oldData.CustomerApiCredentialIds;

        ReportingGroupApi.UpdateReportingGroupAsync(reportingGroupId, name, customerApiCredentialIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setReportingGroups(null);
            });
    }, []);

    const removeRow = useCallback((e: RowRemovingEvent<ReportingGroupDto, any>) => {
        if (e.data.IsDefault) {
            alert("The default reporting cannot be deleted.");
            e.cancel = true;
            return;
        }

        ReportingGroupApi.DeleteReportingGroupAsync(e.data.ReportingGroupId)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setReportingGroups(null);
            });
    }, []);

    const refresh = useCallback(() => {
        setReportingGroups(null);
    }, []);

    // Composant DropDownBox avec DataGrid interne
    const GridInDropDown = useCallback((cellInfo: any) => (
        <GridInDropDownCell cellInfo={cellInfo} customerApiCredentialListDto={customerApiCredentialListDto} />
    ), [customerApiCredentialListDto]);

    const customerApiCredentialTagsCell = useCallback((cellData: any) => {
        const customerApiCredentialIds = cellData.value || [];

        const selectedItems = (customerApiCredentialListDto ?? []).filter(c =>
            customerApiCredentialIds.includes(c.Id)
        );

        return (
            <div style={{ flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px' }}>
                {selectedItems.length === 0 ? (
                    <span>{formatMessage("ReportingGroupsGrid_NoCredentials")}</span>
                ) : (
                    selectedItems.map((cac, idx, arr) => (
                        <span key={cac.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                            {cac.Name}
                        </span>
                    ))
                )}
            </div>
        );
    }, [customerApiCredentialListDto]);

    // customerApiCredentialListDto peut encore etre null quand les groupes arrivent en premier :
    // lever une exception ici casserait le rendu interne de la grille.
    const calculateCredentialsDisplayValue = useCallback((rowData: ReportingGroupDto) => {
        if (!rowData.CustomerApiCredentialIds || rowData.CustomerApiCredentialIds.length === 0) {
            return formatMessage("ReportingGroupsGrid_NoCredentials");
        }
        return rowData.CustomerApiCredentialIds
            .map((id: number) => {
                const cac = (customerApiCredentialListDto ?? []).find(c => c.Id === id);
                return cac ? cac.Name : '';
            })
            .filter(Boolean)
            .join(', ');
    }, [customerApiCredentialListDto]);

    const renderMasterDetail = useCallback((detailData: any) => (
        <ReportingGroupPathTemplate
            key={detailData.key}
            data={detailData}
            refresh={refresh}
        />
    ), [refresh]);

    const editingTexts = useMemo(() => ({ addRow: 'Add a new group' }), []);

    return (
        <div className="reporting-groups-container">
            <div className={"reporting-groups-internal-container"}>
                <div className={"reporting-groups-title-container"}>
                    <h2>{"Reporting"}</h2>
                </div>
                <div>
                    <DataGrid
                        id="grid-container"
                        dataSource={gridDataSource}
                        keyExpr="ReportingGroupId"
                        showBorders={true}
                        onRowInserting={insertRow}
                        onRowUpdating={updateRow}
                        onRowRemoving={removeRow}>

                        <Editing
                            mode="row"
                            allowUpdating={true}
                            allowDeleting={true}
                            allowAdding={true}
                            texts={editingTexts} />

                        <Column dataField="Name" width={270} caption="Name">
                            <RequiredRule message="This field is required." />
                        </Column>
                        <Column dataField="IsDefault" caption="Default" width={100} dataType="boolean" allowEditing={false} />

                        <Column
                            dataField="CustomerApiCredentialIds"
                            caption={formatMessage("ReportingGroupsGrid_Credentials")}
                            editCellRender={GridInDropDown}
                            cellRender={customerApiCredentialTagsCell}
                            calculateDisplayValue={calculateCredentialsDisplayValue}
                        />

                        <MasterDetail
                            enabled={true}
                            render={renderMasterDetail}
                        />
                    </DataGrid>
                </div>
            </div>
        </div>
    );
}
